import '@xyflow/react/dist/style.css';
import "@silevis/reactgrid/styles.css";
import { Flex, message, Select, Space, Tag, Typography, Upload } from 'antd';
import { CloudUploadOutlined } from '@ant-design/icons';
import { Dataset } from './Datasets';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { StoreState } from 'src/store/configureStore';
import { StyledButton } from 'src/styled_components/StyledButton';
import XLSX from 'xlsx'
import { modelEvaluationRequest, operationDetailsRequest, selectedModelsRequest } from 'src/store/actions/operations';
import { useParams } from 'react-router-dom';
import { AsyncStates } from 'src/constants';
import { detectPeaks } from './peakDetection';
import { SavedModelsTable } from './Analysis';
import { ResultsTable } from './Results';
import { StyledInput } from 'src/styled_components/StyledInput';


export const Evaluation = ({ currentStep, previous, next, steps }: any) => {
  const { anid, opid } = useParams<{ anid: string, opid: string }>()

  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(operationDetailsRequest({ opid }))
  }, [dispatch, opid])

  const details = useSelector((state: StoreState) => state.operations.operationDetailsData)
  const detailsStatus = useSelector((state: StoreState) => state.operations.operationDetailsStatus)
  const analysis = useSelector((state: StoreState) => state.operations.analysisDetailsData)
  const analysisStatus = useSelector((state: StoreState) => state.operations.analysisDetailsStatus)

  const [iteration, setIteration] = useState<string | undefined>("new")
  const [currentDatasetId, setCurrentDatasetId] = useState<string | undefined>()
  const [datasets, setDatasets] = useState<any[]>([])
  const [evaluationFiles, setEvaluationFiles] = useState<any>({});

  useEffect(() => {
    const newDatasets = details?.datasets?.map((_dataset: any) => ({ id: crypto.randomUUID(), training_dataset: _dataset.id })) || []
    setCurrentDatasetId(newDatasets?.[0]?.id)
    setDatasets(newDatasets || [])
  }, [details.datasets])

  const datasetEntry = useMemo(() => datasets.find((dataset: any) => dataset.id === currentDatasetId), [datasets, currentDatasetId])
  const trainingDatasetEntry = useMemo(() => details?.datasets?.find((dataset: any) => dataset.id === datasetEntry?.training_dataset), [datasetEntry, details?.datasets])

  const peakDataset = useCallback((peakParameters: any, dataset: any) => {
    const experiments = dataset?.experiments || []
    const features = dataset?.features || []
    const data = dataset?.data || []

    const spectrums = experiments
      ?.map((expId: string) => ({
        x: features,
        y: features?.map((feature: string) => data?.[experiments?.indexOf(expId)]?.[feature]).map(Number)
      })
      )

    const peakSettings = {
      coalesce: 2,
      lookaround: 8,
      sensitivity: 0,
      full: false
    }

    const experimentPeakIndexesList: any = experiments
      ?.map((_: any, index: number) => detectPeaks(spectrums[index]?.y || [], null, peakSettings) || [])

    const experimentPeaksList: any = experimentPeakIndexesList
      ?.map((experimentPeakIndexes: number[], experimentIndex: number) => {
        const peaks = experimentPeakIndexes.map(peakIndex => {
          const peak = features?.[peakIndex]
          const wavelength = peak
          const intensity = peak && data?.[experimentIndex][peak]
          return { wavelength: wavelength, intensity }
        })
        return peaks
      })

    const peakDataset = {
      features: peakParameters,
      experiments,
      data: experiments?.map((experimentId: string, experimentIndex: number) => ({
        ...peakParameters?.reduce((acc: any, peakParameter: string) => {
          const peakParameterIndex = peakParameters.findIndex((_peakParameter: string) => peakParameter === _peakParameter)
          return ({
            ...acc,
            [peakParameter]: experimentPeaksList[experimentIndex][peakParameterIndex]?.intensity
          }
          )
        }, {})
      })
      )
    }

    return peakDataset
  }, [])

  const transform = useCallback(async (record: any) => {
    const fileSheets = await record.arrayBuffer()
    const workBook = XLSX.read(fileSheets, { dense: true, sheets: 0 })
    const config = { featureRowIndex: 0, experimentColumnIndex: 0 }
    const sheet = workBook.Sheets[workBook.SheetNames[0]]
    const range = XLSX.utils.decode_range(sheet["!ref"] ?? "")
    const featureRange = XLSX.utils.encode_range({
      ...range,
      s: { c: config.experimentColumnIndex + 1, r: config.featureRowIndex + 1 },
    })
    const features = sheet[config.featureRowIndex]
      .slice(config.experimentColumnIndex + 1)
      .map((column: XLSX.CellObject) => column.v)
      .map(String)
    const experiments = sheet
      .slice(config.featureRowIndex + 1)
      .map((row: XLSX.CellObject[]) => row[config.experimentColumnIndex].v)
      .map(String)
    const trainingDatasetId = datasets.find((dataset: any) => dataset.id === currentDatasetId)?.training_dataset
    const trainingSamples = details?.datasets.find((dataset: any) => dataset.id === trainingDatasetId)?.dataset?.samples
    let notFound: any[] = []
    const validFeatures = features
      .map((feature: string) => {
        const found = trainingSamples.find((sample: any) => sample.name === feature)
        if (found) return found?.id
        notFound.push(feature)
        return null
      })
      .filter(Boolean)
    if (notFound.length && trainingDatasetEntry?.type === "DATASET") {
      message.error("Data mismatch!")
    } else {
      const data = XLSX.utils.sheet_to_json(sheet, {
        blankrows: false,
        range: featureRange,
        header: features,
        raw: false
      })
      const newData = data.map(row => ({
        ...Object.entries(row as any).reduce((a, [k, v]) => ({
          ...a,
          [k]: v
        }), {})
      }))
      const mappedData = newData.map((record: any) => {
        const updatedRecord: any = {};
        for (const name in record) {
          const newKey = trainingSamples.find((sample: any) => sample.name === name)?.id || name; // Lookup id or keep the same key if not found
          updatedRecord[newKey] = record[name];
        }
        return updatedRecord;
      });
      const dataset = {
        experiments,
        features: validFeatures,
        data: mappedData
      }
      if (trainingDatasetEntry?.type === "NIR_DATASET") {
        const peakParameters = trainingDatasetEntry?.peak_dataset?.features || []
        const peak_dataset = peakDataset(peakParameters, dataset)
        setDatasets(state => state?.map(_dataset => {
          if (_dataset.id !== currentDatasetId) return _dataset
          return {
            ..._dataset,
            dataset,
            peak_dataset
          }
        }))
      } else {
        setDatasets(state => state?.map(_dataset => {
          if (_dataset.id !== currentDatasetId) return _dataset
          return {
            ..._dataset,
            dataset
          }
        }))
      }
    }

    return false

  }, [currentDatasetId, details?.datasets, trainingDatasetEntry, datasets, peakDataset]);

  const props = useMemo(() => ({
    onRemove: (record: any) => {
    },
    accept: '.xlsx, .xls, .csv',
    beforeUpload: (record: any) => {
      setEvaluationFiles((files: any) => ({ ...files, [currentDatasetId!]: record }))
      transform(record)
      return false
    },
    multiple: false,
    fileList: [evaluationFiles[currentDatasetId!]].filter(Boolean),
    maxCount: 1,
  }), [transform, currentDatasetId, evaluationFiles])

  const evaluate = () => {
    dispatch(modelEvaluationRequest({ datasets: datasets.filter((_dataset: any) => _dataset.dataset), anid, name: evaluationName }))
    next()
  }

  useEffect(() => {
    dispatch(selectedModelsRequest(anid));
  }, [anid, dispatch])

  const evaluationData = useMemo(() => {
    return analysis?.evaluations?.find?.((evaluation: any) => evaluation?.id === iteration)
  }, [analysis?.evaluations, iteration]);

  const [evaluationName, setEvaluationName] = useState<string | undefined>(undefined);

  return (
    <Space direction="vertical" size="large" style={{ width: "100%", overflow: "auto" }}>
      <Select
        size='large'
        loading={analysisStatus === AsyncStates.LOADING}
        defaultValue={iteration}
        value={iteration}
        style={{ width: 200 }}
        onChange={(value: string) => {
          setIteration(value)
        }}
        options={[...(analysis?.evaluations?.map((evaluation: any, index: number) => ({ value: evaluation.id, label: <Space><Typography.Text>{evaluation.name ?? `Iteration ${index + 1}`}</Typography.Text><Tag color={evaluation.status === "completed" ? 'green' : 'orange'}>{evaluation.status}</Tag></Space> })) || []), { value: "new", label: <Typography.Text type='secondary'>New Evaluation</Typography.Text> }]}
      />
      {evaluationData?.status === "completed" ? <ResultsTable evaluationData={{ ...{ id: evaluationData?.id }, ...evaluationData?.results }} />
        : <>
          <SavedModelsTable />
          <Typography.Title level={5}>Model Testing Data Input</Typography.Title>
          <Typography.Text type='secondary'>Initiate forward prediction and evaluate the models</Typography.Text>
          <Typography.Text type="secondary">Type:{" "}</Typography.Text>
          <Select
            loading={detailsStatus === AsyncStates.LOADING}
            size="small"
            defaultValue={currentDatasetId}
            value={currentDatasetId}
            style={{ width: 180 }}
            onChange={(value: string) => {
              setCurrentDatasetId(value)
            }}
            options={datasets?.map((dataset: any) => ({ value: dataset.id, label: details?.datasets?.find((_dataset: Dataset) => _dataset.id === dataset.training_dataset)?.name }))}
          />
          <Upload.Dragger {...props} disabled={detailsStatus === AsyncStates.LOADING}>
            <p className="ant-upload-drag-icon">
              <CloudUploadOutlined />
            </p>
            <p className="ant-upload-text">Click to Upload or Drag and Drop </p>
            <p className="ant-upload-hint">CSV or Excel File only</p>
          </Upload.Dragger>
          <Flex style={{ marginTop: 24 }} justify="space-between">
            <div>
              {currentStep > 0 && (
                <StyledButton size="small" style={{ margin: '0 8px' }} onClick={previous}>
                  Previous
                </StyledButton>
              )}
            </div>
            <Space>
              <StyledInput placeholder='Name' size='small' onChange={e => { setEvaluationName(e.target.value) }} />
              {currentStep < steps.length - 1 && (
                <StyledButton size="small" type="primary" onClick={evaluate}>
                  Evaluate
                </StyledButton>
              )}
            </Space>
          </Flex>
        </>}
    </Space>
  )
}
